import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* FETCH WITH SEARCH */
export const fetchSubCategories = createAsyncThunk(
  "subCategory/fetch",
  async ({ page = 1, limit = 10, search = "" }) => {
    const res = await api.get(
      `/api/subcategories?page=${page}&limit=${limit}&search=${search}`
    );
    return res.data;
  }
);

/* FETCH ALL SUBCATEGORIES (NO PAGINATION) */
export const fetchAllSubCategories = createAsyncThunk(
  "subCategory/fetchAll",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/api/subcategories/all`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch all subcategories"
      );
    }
  }
);

/* CREATE */
export const createSubCategory = createAsyncThunk(
  "subCategory/create",
  async (formData, thunkAPI) => {
    try {
      const res = await api.post(
        `/api/subcategories`,
        formData // ✅ FormData (name, categoryId, image)
      );
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create subcategory"
      );
    }
  }
);

/* UPDATE */
export const updateSubCategory = createAsyncThunk(
  "subCategory/update",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(
        `/api/subcategories/${id}`,
        formData // ✅ FormData
      );
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update subcategory"
      );
    }
  }
);

/* DELETE */
export const deleteSubCategory = createAsyncThunk(
  "subCategory/delete",
  async (id) => {
    await api.delete(`/api/subcategories/${id}`);
    return id;
  }
);

/* TOGGLE STATUS */
export const toggleSubCategoryStatus = createAsyncThunk(
  "subCategory/toggle",
  async (id) => {
    const res = await api.patch(
      `/api/subcategories/${id}/status`
    );
    return res.data.data;
  }
);

/* FETCH BY CATEGORY */
export const fetchSubCategoriesByCategory = createAsyncThunk(
  "subCategory/fetchByCategory",
  async (categoryId, thunkAPI) => {
    try {
      const res = await api.get(
        `/api/subcategories/by-category/${categoryId}`
      );
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch subcategories");
    }
  }
);

const subCategorySlice = createSlice({
  name: "subCategory",
  initialState: {
    subCategories: [],
    allSubCategories: [], // New state for all subcategories
    loading: false,
    error: null,
    pagination: {}
  },
  reducers: {
    clearSubCategories: (state) => {
      state.subCategories = [];
    },
    clearAllSubCategories: (state) => {
      state.allSubCategories = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.subCategories = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchSubCategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllSubCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllSubCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.allSubCategories = action.payload.data || action.payload;
      })
      .addCase(fetchAllSubCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSubCategory.fulfilled, (state, action) => {
        state.subCategories.unshift(action.payload);
        // Also add to allSubCategories if it exists
        if (state.allSubCategories.length > 0) {
          state.allSubCategories.unshift(action.payload);
        }
      })
      .addCase(updateSubCategory.fulfilled, (state, action) => {
        const index = state.subCategories.findIndex(
          (sub) => sub._id === action.payload._id
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload;
        }
        // Update in allSubCategories too
        const allIndex = state.allSubCategories.findIndex(
          (sub) => sub._id === action.payload._id
        );
        if (allIndex !== -1) {
          state.allSubCategories[allIndex] = action.payload;
        }
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.subCategories = state.subCategories.filter(
          (sub) => sub._id !== action.payload
        );
        // Remove from allSubCategories too
        state.allSubCategories = state.allSubCategories.filter(
          (sub) => sub._id !== action.payload
        );
      })
      .addCase(toggleSubCategoryStatus.fulfilled, (state, action) => {
        const index = state.subCategories.findIndex(
          (sub) => sub._id === action.payload._id
        );
        if (index !== -1) {
          state.subCategories[index] = action.payload;
        }
        // Update in allSubCategories too
        const allIndex = state.allSubCategories.findIndex(
          (sub) => sub._id === action.payload._id
        );
        if (allIndex !== -1) {
          state.allSubCategories[allIndex] = action.payload;
        }
      })
      .addCase(fetchSubCategoriesByCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubCategoriesByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.subCategories = action.payload;
      })
      .addCase(fetchSubCategoriesByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearSubCategories, clearAllSubCategories } = subCategorySlice.actions;
export default subCategorySlice.reducer;